import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeftOutlined, CheckCircleOutlined } from "@ant-design/icons";
import {
  Form,
  Input,
  Button,
  Select,
  Checkbox,
  CheckboxChangeEvent,
} from "antd";
import {
  FarmRepository,
  FarmRepositoryImpl,
} from "@/features/farm/data/repositories/farm-repositories";
import { QUERY_KEYS } from "@/features/farmer/domain/farmer-enum";
import { City, Province } from "@/features/farm/domain/farm.interface";
import { QUERY_KEYS as QUERY_KEYS_FARM } from "@/features/farm/domain/farm.enum";
import { FarmerRepositoryImpl } from "@/features/farmer/data/repositories/farmer-repositories";
import { FarmerPartialData } from "@/features/login/domain/interface";

interface FarmerPartialRegisterationProps {
  setShowOtpModal: React.Dispatch<React.SetStateAction<boolean>>;
  setPhoneNumber: React.Dispatch<React.SetStateAction<string>>;
  setFarmerPartialData: React.Dispatch<
    React.SetStateAction<FarmerPartialData | null>
  >;
}

const FarmerPartialRegisteration = ({
  setShowOtpModal,
  setPhoneNumber,
  setFarmerPartialData,
}: FarmerPartialRegisterationProps) => {
  const [form] = Form.useForm();

  const [province, setProvince] = useState();

  const initialValue = {
    firstName: "",
    lastName: "",
    nationalCode: "",
    phoneNumber: null,
    provinceId: null,
    cityId: null,
  };

  const farmRepository: FarmRepository = useMemo(() => {
    return new FarmRepositoryImpl();
  }, []);

  const citiesQuery = useQuery<City[] | undefined>({
    queryKey: [QUERY_KEYS_FARM.getStaticCities],
    queryFn: farmRepository._getStaticCities,
  });

  const provincesQuery = useQuery<Province[] | undefined>({
    queryKey: [QUERY_KEYS_FARM.getStaticProvinces],
    queryFn: farmRepository._getStaticProvinces,
  });

  const farmerRegisterMutation = useMutation({
    mutationKey: [QUERY_KEYS.sendFarmerRegistration],
    mutationFn: new FarmerRepositoryImpl()._farmerRegistration,
    onSuccess: () => {
      setShowOtpModal(true);
      setFarmerPartialData(form.getFieldsValue(true));
      setPhoneNumber(form.getFieldValue("phoneNumber"));
    },
  });

  const provincesOptions = useMemo(
    () =>
      provincesQuery?.data?.map((item) => {
        return { label: item.name, value: item.id };
      }),
    [provincesQuery.data]
  );

  const citesOptions = useMemo(
    () =>
      citiesQuery?.data
        ?.filter((item) => item.provinceId === province)
        .map((item) => {
          return { label: item.name, value: item.id };
        }),
    [citiesQuery.data, province]
  );

  function submit(_: any) {
    farmerRegisterMutation.mutate(form.getFieldsValue(true));
  }

  const [enableButton, setEnableButton] = useState(false);

  const handleCheckbox = (e: CheckboxChangeEvent) => {
    if (e.target.checked) {
      setEnableButton(true);
    } else {
      setEnableButton(false);
    }
  };

  return (
    <div className="neo agrobg rounded-3xl p-4 w-fit max-lg:w-fit mx-auto my-6 z-[9999999]">
      <Form
        autoComplete="nope"
        initialValues={initialValue}
        form={form}
        className="flex flex-col px-12 max-lg:px-4 items-center pt-8 rounded-[40px] border-dashed border-4 border-white !gap-8 max-lg:gap-4 w-fit relative"
        onFinish={submit}
        layout="vertical"
      >
        <h2 className="text-agrotext font-black text-[42px] max-lg:text-[25px]">
          ثبت نام کشاورز
        </h2>

        <div className="flex flex-wrap max-lg:flex-col gap-5 max-lg:gap-2 w-[34vw] max-lg:w-full !justify-center">
          <Form.Item
            rules={[
              {
                validator(_, value) {
                  return new Promise((resolve, reject) => {
                    if (value?.length >= 2) {
                      resolve(true);
                    } else {
                      reject("نام باید دو حرف یا بیشتر باشد");
                    }
                  });
                },
              },
            ]}
            name="firstName"
          >
            <Input
              autoComplete="nope"
              className="inner-box-hover py-[10px] w-[16vw] max-lg:w-[250px]"
              onChange={(e) => form.setFieldValue("firstName", e.target.value)}
              size="large"
              placeholder="نام "
            />
          </Form.Item>
          <Form.Item
            rules={[
              {
                validator(_, value) {
                  return new Promise((resolve, reject) => {
                    if (value?.length >= 2) {
                      resolve(true);
                    } else {
                      reject("نام خانوادگی باید دو حرف یا بیشتر باشد");
                    }
                  });
                },
              },
            ]}
            name="lastName"
          >
            <Input
              autoComplete="nope"
              className="inner-box-hover py-[10px] w-[16vw] max-lg:w-[250px]"
              onChange={(e) => form.setFieldValue("lastName", e.target.value)}
              size="large"
              placeholder="نام خانوادگی"
            />
          </Form.Item>

          <Form.Item
            rules={[
              {
                required: true,
                message: "کد ملی الزامی است",
              },
              {
                min: 10,
                message: "این قسمت نمیتواند کمتر از 10 رقم باشد",
              },
              {
                max: 10,
                message: "این قسمت نمیتواند بیشتر از 10 رقم باشد",
              },
            ]}
            name="nationalCode"
          >
            <Input
              autoComplete="nope"
              className="inner-box-hover py-[10px] w-[16vw] max-lg:w-[250px]"
              size="large"
              accept="number"
              type="number"
              onChange={(e) =>
                form.setFieldValue("nationalCode", e.target.value)
              }
              placeholder="کد ملی"
            />
          </Form.Item>
          <Form.Item
            rules={[
              {
                required: true,
                message: "شماره همراه الزامی است",
              },

              {
                max: 11,
                message: "لطفا شماره تلفن همراه معتبر وارد نمایید",
              },
            ]}
            name="phoneNumber"
          >
            <Input
              autoComplete="nope"
              className="inner-box-hover py-[10px] w-[16vw] max-lg:w-[250px]"
              size="large"
              accept="number"
              type="number"
              onChange={(e) =>
                form.setFieldValue("phoneNumber", e.target.value)
              }
              placeholder="شماره تلفن"
            />
          </Form.Item>

          <Form.Item
            rules={[
              {
                validator(_, value) {
                  return new Promise((resolve, reject) => {
                    if (value) {
                      resolve(true);
                    } else {
                      reject("انتخاب استان الزامی است");
                    }
                  });
                },
              },
            ]}
            name="provinceId"
          >
            <Select
              showSearch
              optionFilterProp="label"
              className="button-neo  !h-[40px] !w-[16vw] text-center  max-lg:!w-[250px] !rounded-[60px]"
              onChange={(value) => {
                setProvince(value);
                form.setFieldValue("provinceId", value);
                form.setFieldValue("cityId", null);
              }}
              options={provincesOptions}
              placeholder="استان"
              size="large"
            />{" "}
          </Form.Item>

          <Form.Item
            rules={[
              {
                validator(_, value) {
                  return new Promise((resolve, reject) => {
                    if (value) {
                      resolve(true);
                    } else {
                      reject("انتخاب شهر الزامی است");
                    }
                  });
                },
              },
            ]}
            name="cityId"
          >
            <Select
              showSearch
              optionFilterProp="label"
              className="button-neo !h-[40px] !w-[16vw] max-lg:!w-[250px] !rounded-[60px]"
              onChange={(value) => {
                form.setFieldValue("cityId", value);
              }}
              options={citesOptions}
              placeholder="شهر"
              size="large"
              allowClear
            />{" "}
          </Form.Item>

          <Checkbox
            className="flex flex-row items-center justify-center font-body w-fit text-right text-[#404848] text-sm max-lg:text-xs font-medium"
            name="premonitionRobot"
            onChange={handleCheckbox}
            checked={enableButton}
          >
            ضمن مطالعه{" "}
            <Link to="https://agroyaar.com/terms">
              <span className="text-green-700 text-sm font-medium hover:brightness-125 ">
                قوانین استفاده از اگرویار
              </span>
            </Link>{" "}
            و{" "}
            <Link to="https://agroyaar.com/privacy">
              <span className="text-green-700 text-sm font-medium hover:brightness-125 ">
                شرایط حریم خصوصی
              </span>
            </Link>
            ، آن را می‌پذیرم.
          </Checkbox>
        </div>

        <Form.Item htmlFor="submit">
          <Button
            disabled={!enableButton}
            htmlType="submit"
            loading={farmerRegisterMutation.isLoading}
            shape="round"
            className="w-[32vw] max-lg:w-[200px] h-[40px] button-green text-white font-bold text-[1.2rem]"
            dir="ltr"
            icon={<CheckCircleOutlined />}
          >
            ثبت نام
          </Button>
        </Form.Item>
        <Link
          to={"/"}
          className="ball flex items-center justify-center rounded-full w-10 h-10 text-[#bbbbbb] font-extrabold text-2xl absolute left-5 top-5"
          dir="ltr"
        >
          <ArrowLeftOutlined />
        </Link>
      </Form>
    </div>
  );
};

export default FarmerPartialRegisteration;
